import type {
  Annotation,
  LoadResponse,
  Query,
  TransformedQuery,
} from "@cubejs-client/core";
import { z } from "zod/v4";
import { tupleOf } from "../../../util/zod-tuple";
import {
  type CubeMember,
  type CubeMeta,
  dimensionFormat,
  precision,
} from "./cube-meta";
import { cubeQuerySchema, VALUE_SCHEMAS } from "./cube-query";

type MemberType = CubeMember["type"];

export type CubeRow = Record<string, string | null>;

const COMPARE_DATE_RANGE = "compareDateRange";

const QUERY_TYPES = [
  "regularQuery",
  "compareDateRangeQuery",
  "blendingQuery",
] as const;
const PRE_AGGREGATION_TYPES = [
  "rollup",
  "rollupJoin",
  "rollupLambda",
  "originalSql",
] as const;

const LEAF_MEASURE_TYPES = [
  "count",
  "countDistinct",
  "sum",
  "min",
  "max",
  "number",
  "countDistinctApprox",
] as const satisfies readonly NonNullable<
  TransformedQuery["measureToLeafMeasures"]
>[string][number]["type"][];

const WIRE_VALUES: Record<MemberType, z.ZodType<string>> = {
  ...VALUE_SCHEMAS,
  time: z.string().regex(/^\d{4}-\d{2}-\d{2}T00:00:00\.000$/, {
    message: "not-a-day",
  }),
};

// Cube types these as one-pair tuples.
const pairs = <T>(second: z.ZodType<T>) =>
  z
    .tuple([z.tuple([z.string(), second])])
    .transform(([[key, value]]): [[string, T]] => [[key, value]])
    .nonoptional();

const transformedQuery = z.object({
  allFiltersWithinSelectedDimensions: z.boolean(),
  granularityHierarchies: z.record(z.string(), z.array(z.string())),
  hasMultipliedMeasures: z.boolean(),
  hasNoTimeDimensionsWithoutGranularity: z.boolean(),
  isAdditive: z.boolean(),
  leafMeasureAdditive: z.boolean(),
  leafMeasures: z.array(z.string()),
  measures: z.array(z.string()),
  sortedDimensions: z.array(z.string()),
  sortedTimeDimensions: pairs(z.string()),
  measureToLeafMeasures: z
    .record(
      z.string(),
      z.array(
        z.object({
          measure: z.string(),
          additive: z.boolean(),
          type: z.enum(LEAF_MEASURE_TYPES),
        }),
      ),
    )
    .optional(),
  ownedDimensions: z.array(z.string()),
  ownedTimeDimensionsAsIs: pairs(z.string().nullable()),
  ownedTimeDimensionsWithRollupGranularity: pairs(z.string()),
});

const formatBaseName = z.enum([
  "number",
  "percent",
  "currency",
  "abbr",
  "accounting",
]);

const annotation = z.object({
  title: z.string(),
  shortTitle: z.string(),
  type: z.string(),
  meta: z.unknown().optional(),
  format: dimensionFormat.optional(),
  formatDescription: z
    .object({
      name: z.union([
        z.enum(["custom", "id"]),
        formatBaseName,
        z.templateLiteral([formatBaseName, "_", precision]),
      ]),
      specifier: z.string(),
      currency: z.string().optional(),
    })
    .optional(),
  currency: z.string().optional(),
  drillMembers: z.array(z.unknown()).optional(),
  drillMembersGrouped: z.unknown().optional(),
  granularity: z
    .object({
      name: z.string(),
      title: z.string(),
      interval: z.string(),
      offset: z.string().optional(),
      origin: z.string().optional(),
    })
    .optional(),
}) satisfies z.ZodType<Annotation>;

const annotations = z.object({
  dimensions: z.record(z.string(), annotation),
  measures: z.record(z.string(), annotation),
  timeDimensions: z.record(z.string(), annotation),
  segments: z.record(z.string(), annotation),
});

// A time dimension with a granularity comes back under its bucket key and its plain name, as Cube returns it.
const columnsOf = ({
  query,
  meta,
}: {
  query: Query;
  meta: CubeMeta;
}): [string, MemberType][] => {
  const types = new Map(
    meta.cubes
      .flatMap((cube): CubeMember[] => [...cube.measures, ...cube.dimensions])
      .map((member): [string, MemberType] => [member.name, member.type]),
  );
  return [
    ...[...(query.measures ?? []), ...(query.dimensions ?? [])].flatMap(
      (name): [string, MemberType][] => {
        const type = types.get(name);
        return type === undefined ? [] : [[name, type]];
      },
    ),
    ...(query.timeDimensions ?? []).flatMap(
      (timeDimension): [string, MemberType][] => {
        const { dimension, granularity } = timeDimension;
        const buckets: [string, MemberType][] =
          granularity === undefined
            ? []
            : [
                [`${dimension}.${granularity}`, "time"],
                [dimension, "time"],
              ];
        return "compareDateRange" in timeDimension
          ? [...buckets, [COMPARE_DATE_RANGE, "string"]]
          : buckets;
      },
    ),
  ];
};

// Cube names the compared range as the first dimension of a compared query's pivot.
const withoutCompareDateRange = (pivot: unknown) =>
  typeof pivot === "object" &&
  pivot !== null &&
  "dimensions" in pivot &&
  Array.isArray(pivot.dimensions)
    ? {
        ...pivot,
        dimensions: pivot.dimensions.filter(
          (dimension) => dimension !== COMPARE_DATE_RANGE,
        ),
      }
    : pivot;

function resultSchema({ query, meta }: { query: Query; meta: CubeMeta }) {
  const row = z
    .object(
      Object.fromEntries(
        columnsOf({ query, meta }).map(([name, type]) => [
          name,
          WIRE_VALUES[type].nullable(),
        ]),
      ),
    )
    .pipe(z.record(z.string(), z.string().nullable()));
  return z
    .object({
      annotation: annotations,
      lastRefreshTime: z.string(),
      query: cubeQuerySchema(meta),
      data: z.array(row),
      // A predicate keeps the key required without strictNullChecks, where a nullable infers optional.
      external: z.custom<boolean | null>(
        (value) => typeof value === "boolean" || value === null,
        { message: "not-a-boolean-or-null" },
      ),
      dbType: z.string(),
      extDbType: z.string(),
      requestId: z.string().optional(),
      usedPreAggregations: z
        .record(
          z.string(),
          z.object({
            preAggregationId: z.string().optional(),
            targetTableName: z.string().optional(),
            lastUpdatedAt: z.number().optional(),
            type: z.enum(PRE_AGGREGATION_TYPES),
          }),
        )
        .optional(),
      transformedQuery: transformedQuery.optional(),
      total: z.number().int().min(0).optional(),
      slowQuery: z.boolean().optional(),
    })
    .refine((parsed) => query.total !== true || parsed.total !== undefined, {
      message: "missing-total",
    });
}

/** Mirrors the load response Cube's client hands back, with each row typed by the query's members. */
export function cubeLoadResponseSchema({
  query,
  meta,
}: {
  query: Query;
  meta: CubeMeta;
}) {
  const echoed = cubeQuerySchema(meta);
  return z.looseObject({
    queryType: z.enum(QUERY_TYPES),
    results: z.array(resultSchema({ query, meta })),
    pivotQuery: z.intersection(
      z.preprocess(withoutCompareDateRange, echoed),
      z.object({ queryType: z.enum(QUERY_TYPES) }),
    ),
    slowQuery: z.boolean().optional(),
  }) satisfies z.ZodType<LoadResponse<CubeRow>>;
}

/** Mirrors Cube's response to a blending load: one result per query, in the order the queries were sent. */
export function cubeBlendResponseSchema({
  queries: [first, ...rest],
  meta,
}: {
  queries: readonly [Query, ...Query[]];
  meta: CubeMeta;
}) {
  return z.looseObject({
    queryType: z.literal("blendingQuery"),
    results: tupleOf([
      resultSchema({ query: first, meta }),
      ...rest.map((query) => resultSchema({ query, meta })),
    ]),
    // A blend's pivot names a synthetic "time" dimension no view has, so only its type is read.
    pivotQuery: z.looseObject({ queryType: z.literal("blendingQuery") }),
    slowQuery: z.boolean().optional(),
  }) satisfies z.ZodType<LoadResponse<CubeRow>>;
}

export type CubeLoadResponse = z.output<
  ReturnType<typeof cubeLoadResponseSchema>
>;

export type CubeBlendResponse = z.output<
  ReturnType<typeof cubeBlendResponseSchema>
>;
